import { Dictionary } from '@ngrx/entity';
import { DataManipulationConfig, FilterFieldDefinition, NestedDataInfo } from '../../models';
import { isFilterFieldGroup } from '../../utils';

export interface ItemData {
  nestedData: Dictionary<NestedDataInfo>;
}

export interface DataIdsMapping {
  [dataId: string]: ItemData;
}

export interface OptionIndex {
  dataIds: DataIdsMapping;
  iconPath?: string;
}

export interface OptionsIndex {
  [optionValue: string]: OptionIndex;
}

export interface FieldIndex {
  optionsIndex: OptionsIndex;

  allNestedDataMapping: DataIdsMapping;
}

export interface FieldsIndex {
  [fieldId: string]: FieldIndex;
}

export interface FilterIndex {
  fieldsIndex: FieldsIndex;
}

function handlePropertySelector(
  filterDef: FilterFieldDefinition<any>,
  dataItem: any
): { data: string; icon?: string }[] {
  const propertySelectorValue = filterDef.propertySelector(dataItem);
  let propertySelectorIcon: string;
  if (filterDef.iconsSelector) {
    propertySelectorIcon = filterDef.iconsSelector(dataItem);
  }
  let result: any[] = Array.isArray(propertySelectorValue) ? propertySelectorValue : [propertySelectorValue];

  let resultIcons: any[];
  if (propertySelectorIcon) {
    resultIcons = Array.isArray(propertySelectorIcon) ? propertySelectorIcon : [propertySelectorIcon];
  }

  const data = result.filter((value) => value !== undefined && value !== null).map((value) => `${value}`);
  const icons = resultIcons?.filter((value) => value !== undefined && value !== null).map((value) => `${value}`);
  return data.map((dataString, index) => ({ data: dataString, icon: icons ? icons[index] : undefined }));
}

function getOrCreateOptionIndex(
  fieldIndex: FieldIndex,
  optionValue: string,
  currentDataId: string,
  optionIcon?: string
): OptionIndex {
  let optionIndex: OptionIndex = fieldIndex.optionsIndex[optionValue];

  if (!optionIndex) {
    fieldIndex.optionsIndex[optionValue] = { dataIds: {}, iconPath: optionIcon };
    optionIndex = fieldIndex.optionsIndex[optionValue];
  }

  if (!optionIndex.dataIds[currentDataId]) {
    optionIndex.dataIds[currentDataId] = { nestedData: {} };
  }

  return optionIndex;
}

export function createFilterIndex(data: any[], filterConfig: DataManipulationConfig): FilterIndex {
  const index: FilterIndex = { fieldsIndex: {} };
  const fieldsIndex: FieldsIndex = index.fieldsIndex;

  data.forEach((dataItem) => {
    const currentParentDataId = dataItem[filterConfig.idPropertyName];

    filterConfig.fields.flatMap((fieldOrGroup) => isFilterFieldGroup(fieldOrGroup)).forEach((filterDefinitionItem) => {
      let fieldIndex: FieldIndex = fieldsIndex[filterDefinitionItem.fieldId];

      if (!fieldIndex) {
        fieldsIndex[filterDefinitionItem.fieldId] = { optionsIndex: {}, allNestedDataMapping: {} };
        fieldIndex = fieldsIndex[filterDefinitionItem.fieldId];
      }

      if (filterDefinitionItem.nestedDataDefinition) {
        const dataFromParentPropertySelector: any[] = filterDefinitionItem.propertySelector(dataItem);

        let itemData: ItemData = fieldIndex.allNestedDataMapping[currentParentDataId];

        if (!itemData) {
          fieldIndex.allNestedDataMapping[currentParentDataId] = { nestedData: {} };
          itemData = fieldIndex.allNestedDataMapping[currentParentDataId];
        }

        const nestedOptionsDictionary: { [optionValue: string]: NestedDataInfo[] } = {};

        dataFromParentPropertySelector?.forEach((nestedDataItem) => {
          const optionValue = filterDefinitionItem.nestedDataDefinition.propertySelector(nestedDataItem);
          const optionId = filterDefinitionItem.nestedDataDefinition.idSelector(nestedDataItem);
          const prev = nestedOptionsDictionary[optionValue] || [];

          if (Array.isArray(optionValue)) {
            optionValue.forEach((value) => {
              itemData.nestedData[optionId] = {
                id: optionId,
                optionValue: value
              };
              nestedOptionsDictionary[value] = [
                ...prev,
                {
                  id: optionId,
                  optionValue: value,
                },
              ];
            });
          } else {
            itemData.nestedData[optionId] = {
              id: optionId,
              optionValue
            };
            nestedOptionsDictionary[optionValue] = [
              ...prev,
              {
                id: optionId,
                optionValue
              },
            ];
          }
        }, {});

        Object.keys(nestedOptionsDictionary).forEach((optionValue) => {
          const currentOptionIndex = getOrCreateOptionIndex(fieldIndex, optionValue, currentParentDataId);
          const nestedDataInfo = nestedOptionsDictionary[optionValue];

          nestedDataInfo.forEach((nestedData) => {
            currentOptionIndex.dataIds[currentParentDataId].nestedData[nestedData.id] = nestedData;
          });
        });
      } else {
        handlePropertySelector(filterDefinitionItem, dataItem).forEach((optionValue) =>
          getOrCreateOptionIndex(fieldIndex, optionValue.data, currentParentDataId, optionValue.icon)
        );
      }
    });
  });

  return index;
}
